/**
 * People.
 *
 * Replaces the capsule-with-a-sphere-on-top that stood in for a character.
 * That placeholder also made the character creator dishonest: it offers three
 * body types, six faces, eight hairstyles, nine hair colours, ten skin tones
 * and four outfits, and the renderer showed exactly one of them. Everything the
 * creator promises is drawn here.
 *
 * Built from primitives rather than an imported model on purpose — a rigged
 * mesh would be megabytes over the wire and a licence to keep track of, and at
 * this scale the shape reads fine. What makes a figure look alive is not
 * polygon count, it is that the limbs move when it walks.
 *
 * The rig comes back with its joints exposed so `poseWalk` can drive them.
 */

import type { Scene } from '@babylonjs/core/scene.js';
import type { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator.js';
import { Color3 } from '@babylonjs/core/Maths/math.color.js';
import { Vector3 } from '@babylonjs/core/Maths/math.vector.js';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder.js';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode.js';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial.js';
import type { Mesh } from '@babylonjs/core/Meshes/mesh.js';

/**
 * What this file needs to know about somebody to draw them.
 *
 * Deliberately looser than the shared `Appearance`: a player's appearance
 * satisfies it, and so does an NPC row that only carries three of the five
 * fields. Requiring the full type here would mean inventing a face id for
 * every shopkeeper just to get past the compiler.
 */
export interface FigureLook {
  bodyType?: string;
  face?: string;
  hair?: string;
  hairColour?: string;
  skinTone?: string;
  outfit?: string;
}

/** What the walk cycle needs to move. */
export interface FigureRig {
  root: TransformNode;
  /** Bobs up and down with the stride. */
  body: TransformNode;
  leftArm: TransformNode;
  rightArm: TransformNode;
  leftLeg: TransformNode;
  rightLeg: TransformNode;
  head: TransformNode;
  /** Advances with distance travelled, not with time — see poseWalk. */
  phase: number;
}

/* Outfits. Four sets of three colours: what they wear, what they wear on their
   legs, and their boots. Chosen to sit in the world's palette rather than
   fight it. */
const OUTFITS: Record<string, { tunic: string; trousers: string; boots: string; trim: string }> = {
  plain: { tunic: '#8a7a63', trousers: '#5a4d3d', boots: '#463527', trim: '#6f6152' },
  travelling: { tunic: '#5d6b52', trousers: '#43402f', boots: '#3a2a1c', trim: '#8a7440' },
  working: { tunic: '#a35b32', trousers: '#4a4038', boots: '#3d2b1c', trim: '#d0913f' },
  guild: { tunic: '#3f4f6b', trousers: '#2f3646', boots: '#2a2118', trim: '#c9a227' }
};

/** In the creator's order, so face-1..face-6 map to six different faces. */
const FACES = ['face-1', 'face-2', 'face-3', 'face-4', 'face-5', 'face-6'];

/** Shoulder width and bulk. The creator offers these, so they must be visible. */
const BODIES: Record<string, { shoulders: number; chest: number; limb: number }> = {
  slight: { shoulders: 0.86, chest: 0.88, limb: 0.88 },
  average: { shoulders: 1, chest: 1, limb: 1 },
  broad: { shoulders: 1.18, chest: 1.14, limb: 1.12 }
};

/**
 * Materials are cached by colour across the whole scene.
 *
 * Fifty people in a town would otherwise be four hundred materials, each one a
 * separate shader binding. Sharing them is the difference between a crowd
 * rendering and a crowd stuttering.
 */
function material(scene: Scene, hex: string, shiny = false): StandardMaterial {
  const name = `fig-${hex}${shiny ? '-s' : ''}`;
  const existing = scene.getMaterialByName(name);
  if (existing instanceof StandardMaterial) return existing;

  const mat = new StandardMaterial(name, scene);
  mat.diffuseColor = Color3.FromHexString(hex);
  // Cloth and skin are not mirrors. A default specular highlight is most of
  // what makes untextured primitives look like cheap plastic.
  mat.specularColor = shiny ? new Color3(0.14, 0.13, 0.11) : new Color3(0.03, 0.03, 0.03);
  mat.specularPower = 48;
  return mat;
}

function box(
  scene: Scene, name: string,
  size: { width: number; height: number; depth: number },
  position: Vector3, mat: StandardMaterial, parent: TransformNode
): Mesh {
  const mesh = MeshBuilder.CreateBox(name, size, scene);
  mesh.position.copyFrom(position);
  mesh.material = mat;
  mesh.isPickable = false;
  mesh.parent = parent;
  return mesh;
}

/** Hair, by style. Each is a different silhouette, which is the point of it. */
function buildHair(
  scene: Scene, style: string, colour: string, head: TransformNode
): void {
  if (style === 'shaved') return;
  const mat = material(scene, colour);

  /* A cap that sits ON the skull, not around it.
     The first version was a full sphere slightly wider than the head, centred
     near its middle — which put a coloured ball over the face and hid the eyes
     entirely. Squashed flat, raised to the crown and pushed back off the brow,
     it reads as hair. */
  const cap = MeshBuilder.CreateSphere('hair-cap', { diameter: 0.235, segments: 8 }, scene);
  cap.scaling.set(1, style === 'cropped' ? 0.5 : 0.72, 1.02);
  cap.position.set(0, 0.088, -0.012);
  cap.material = mat;
  cap.isPickable = false;
  cap.parent = head;

  // Fringe: a thin bar across the top of the brow. It stops the cap from
  // floating and gives the face a top edge.
  const fringe = MeshBuilder.CreateBox('hair-fringe',
    { width: 0.225, height: 0.05, depth: 0.235 }, scene);
  fringe.position.set(0, 0.087, 0);
  fringe.material = mat;
  fringe.isPickable = false;
  fringe.parent = head;

  /* The back and sides.
     Everything below is behind or beside the face and never in front of it: an
     earlier version put "volume" lumps at eye level either side of the head,
     which came out as a pair of buns stuck to the ears. */
  const shell = (
    name: string,
    size: { width: number; height: number; depth: number },
    position: [number, number, number]
  ): void => {
    const mesh = MeshBuilder.CreateBox(name, size, scene);
    mesh.position.set(position[0], position[1], position[2]);
    mesh.material = mat;
    mesh.isPickable = false;
    mesh.parent = head;
  };

  // Everything but a crop covers the back of the skull. Without this the head
  // is bare skin from behind, which is the angle you see most of the time.
  if (style !== 'cropped') shell('hair-nape', { width: 0.225, height: 0.19, depth: 0.05 }, [0, 0.01, -0.108]);

  // Sideburns: a thin strip in front of each ear, behind the cheek.
  if (style !== 'cropped') {
    for (const side of [-1, 1]) {
      shell('hair-side', { width: 0.035, height: 0.13, depth: 0.2 }, [side * 0.108, 0.015, -0.012]);
    }
  }

  switch (style) {
    case 'long':
      shell('hair-long', { width: 0.235, height: 0.36, depth: 0.09 }, [0, -0.14, -0.1]);
      for (const side of [-1, 1]) {
        shell('hair-fall', { width: 0.05, height: 0.3, depth: 0.16 }, [side * 0.113, -0.1, -0.03]);
      }
      break;
    case 'braided':
      shell('hair-braid', { width: 0.075, height: 0.42, depth: 0.075 }, [0, -0.19, -0.13]);
      break;
    case 'tied-back':
      shell('hair-tail', { width: 0.075, height: 0.2, depth: 0.11 }, [0, -0.06, -0.16]);
      break;
    case 'wavy':
    case 'curls': {
      // Volume, kept at or above the crown and behind the brow line.
      const size = style === 'curls' ? 0.15 : 0.13;
      for (const [x, y, z] of [
        [-0.085, 0.105, -0.03], [0.085, 0.105, -0.03], [0, 0.09, -0.115]
      ] as const) {
        const lump = MeshBuilder.CreateSphere('hair-lump', { diameter: size, segments: 6 }, scene);
        lump.position.set(x, y, z);
        lump.material = mat;
        lump.isPickable = false;
        lump.parent = head;
      }
      break;
    }
    default:
      break;
  }
}

/**
 * Builds a person.
 *
 * Roughly 1.8 m tall with the origin at the feet, which is what the movement
 * code already assumes.
 */
export function buildFigure(
  scene: Scene,
  appearance: FigureLook | undefined,
  options: { shadows?: ShadowGenerator | null; name?: string } = {}
): FigureRig {
  const look = {
    skinTone: appearance?.skinTone ?? '#cf9463',
    hairColour: appearance?.hairColour ?? '#3a2a1c',
    hair: appearance?.hair ?? 'short',
    face: appearance?.face ?? 'face-1',
    outfit: appearance?.outfit ?? 'plain',
    bodyType: appearance?.bodyType ?? 'average'
  };

  const kit = OUTFITS[look.outfit] ?? OUTFITS['plain']!;
  const build = BODIES[look.bodyType] ?? BODIES['average']!;

  const skin = material(scene, look.skinTone);
  const tunic = material(scene, kit.tunic);
  const trousers = material(scene, kit.trousers);
  const boots = material(scene, kit.boots);
  const trim = material(scene, kit.trim, true);

  const root = new TransformNode(options.name ?? 'figure', scene);

  /* One node the whole body hangs off, so the walk cycle can bob it without
     moving the feet through the ground. */
  const body = new TransformNode('fig-body', scene);
  body.parent = root;

  // --- legs. Pivot at the hip, so rotating swings the whole leg. ---
  const legs: TransformNode[] = [];
  for (const side of [-1, 1]) {
    const hip = new TransformNode(side < 0 ? 'fig-leg-l' : 'fig-leg-r', scene);
    hip.position.set(side * 0.115 * build.limb, 0.86, 0);
    hip.parent = body;

    box(scene, 'thigh',
      { width: 0.17 * build.limb, height: 0.44, depth: 0.19 * build.limb },
      new Vector3(0, -0.22, 0), trousers, hip);
    box(scene, 'shin',
      { width: 0.145 * build.limb, height: 0.42, depth: 0.165 * build.limb },
      new Vector3(0, -0.63, 0), trousers, hip);
    // Boots overhang forward, which is what stops a leg reading as a stick.
    box(scene, 'boot',
      { width: 0.17 * build.limb, height: 0.13, depth: 0.27 },
      new Vector3(0, -0.8, 0.035), boots, hip);
    legs.push(hip);
  }

  // --- torso ---
  box(scene, 'hips',
    { width: 0.36 * build.chest, height: 0.2, depth: 0.23 * build.chest },
    new Vector3(0, 0.94, 0), trousers, body);
  box(scene, 'chest',
    { width: 0.42 * build.shoulders, height: 0.46, depth: 0.26 * build.chest },
    new Vector3(0, 1.26, 0), tunic, body);
  // A belt. One band of a different colour across the middle does more for the
  // silhouette than any amount of extra geometry.
  box(scene, 'belt',
    { width: 0.44 * build.shoulders, height: 0.075, depth: 0.275 * build.chest },
    new Vector3(0, 1.045, 0), trim, body);
  box(scene, 'neck',
    { width: 0.115, height: 0.09, depth: 0.115 },
    new Vector3(0, 1.53, 0), skin, body);

  // --- arms. Pivot at the shoulder. ---
  const arms: TransformNode[] = [];
  for (const side of [-1, 1]) {
    const shoulder = new TransformNode(side < 0 ? 'fig-arm-l' : 'fig-arm-r', scene);
    shoulder.position.set(side * (0.245 * build.shoulders), 1.44, 0);
    shoulder.parent = body;

    box(scene, 'upper-arm',
      { width: 0.135 * build.limb, height: 0.34, depth: 0.145 * build.limb },
      new Vector3(0, -0.17, 0), tunic, shoulder);
    // Forearms are bare: a change of colour at the elbow gives the arm a joint
    // without needing one.
    box(scene, 'forearm',
      { width: 0.115 * build.limb, height: 0.32, depth: 0.125 * build.limb },
      new Vector3(0, -0.5, 0), skin, shoulder);
    box(scene, 'hand',
      { width: 0.115, height: 0.115, depth: 0.1 },
      new Vector3(0, -0.71, 0), skin, shoulder);
    arms.push(shoulder);
  }

  // --- head ---
  const head = new TransformNode('fig-head', scene);
  head.position.set(0, 1.68, 0);
  head.parent = body;

  const skull = MeshBuilder.CreateBox('skull',
    { width: 0.22, height: 0.22, depth: 0.22 }, scene);
  skull.position.y = 0.02;
  skull.material = skin;
  skull.isPickable = false;
  skull.parent = head;

  /* A jaw, narrower and shallower than the skull.
     One extra box, and it is what stops the head being a cube: the silhouette
     now steps in below the cheekbones the way a face does. */
  const jaw = MeshBuilder.CreateBox('jaw',
    { width: 0.175, height: 0.075, depth: 0.19 }, scene);
  jaw.position.set(0, -0.105, 0.008);
  jaw.material = skin;
  jaw.isPickable = false;
  jaw.parent = head;

  /* Ears. Nobody looks at them and everybody notices when a head has none. */
  for (const side of [-1, 1]) {
    const ear = MeshBuilder.CreateBox('ear',
      { width: 0.022, height: 0.06, depth: 0.045 }, scene);
    ear.position.set(side * 0.116, 0.01, -0.01);
    ear.material = skin;
    ear.isPickable = false;
    ear.parent = head;
  }

  /* Eyes. Two dark specks, and they are the whole reason a figure reads as a
     person facing you rather than a shape. Cheap, and worth more than anything
     else on the model. */
  const eyeMat = material(scene, '#241b14');
  const faceIndex = Math.max(0, FACES.indexOf(look.face ?? 'face-1'));
  // Six faces in the creator, six here: the brow rides higher or lower and the
  // eyes sit wider or closer. Small numbers, plainly different people.
  const browY = 0.03 + (faceIndex % 3) * 0.012;
  const eyeSpread = 0.048 + Math.floor(faceIndex / 3) * 0.008;

  for (const side of [-1, 1]) {
    const eye = MeshBuilder.CreateBox('eye',
      { width: 0.042, height: 0.03, depth: 0.02 }, scene);
    eye.position.set(side * eyeSpread, browY - 0.005, 0.111);
    eye.material = eyeMat;
    eye.isPickable = false;
    eye.parent = head;

    // Brow. A darker bar over each eye, which is most of what an expression is.
    const brow = MeshBuilder.CreateBox('brow',
      { width: 0.052, height: 0.016, depth: 0.018 }, scene);
    brow.position.set(side * eyeSpread, browY + 0.032, 0.112);
    brow.material = material(scene, look.hairColour);
    brow.isPickable = false;
    brow.parent = head;
  }

  const nose = MeshBuilder.CreateBox('nose',
    { width: 0.036, height: 0.055, depth: 0.036 }, scene);
  nose.position.set(0, browY - 0.05, 0.118);
  nose.material = skin;
  nose.isPickable = false;
  nose.parent = head;

  const mouth = MeshBuilder.CreateBox('mouth',
    { width: 0.058, height: 0.012, depth: 0.014 }, scene);
  mouth.position.set(0, -0.078, 0.104);
  mouth.material = material(scene, '#7a4238');
  mouth.isPickable = false;
  mouth.parent = head;

  buildHair(scene, look.hair, look.hairColour, head);

  if (options.shadows != null) {
    // Shadow casters are added per mesh, and only the big ones matter — a hand
    // casts nothing anybody will see.
    for (const mesh of root.getChildMeshes()) {
      if (/chest|thigh|shin|skull|hips/.test(mesh.name)) options.shadows.addShadowCaster(mesh);
    }
  }

  return {
    root, body, head,
    leftArm: arms[0]!, rightArm: arms[1]!,
    leftLeg: legs[0]!, rightLeg: legs[1]!,
    phase: 0
  };
}

/**
 * Builds a four-legged beast.
 *
 * Enemies were a capsule with a box stuck on the front for a snout. Standing a
 * creature on four legs and dropping its body to knee height is most of what
 * separates "an animal" from "a person-shaped thing that attacks you" — and it
 * matters for reading a fight, because a wolf at chest height and a bandit at
 * head height are told apart at a glance.
 *
 * `tint` is the creature's colour, already chosen per enemy type by the server.
 */
export function buildBeast(
  scene: Scene,
  tint: string,
  options: { shadows?: ShadowGenerator | null; name?: string } = {}
): FigureRig {
  const hide = material(scene, tint);
  // A darker version of the same hide for the underside, legs and muzzle. One
  // hue, two values: it reads as one animal rather than a colour scheme.
  const darkHex = Color3.FromHexString(tint).scale(0.62).toHexString();
  const dark = material(scene, darkHex);
  const claw = material(scene, '#d8cdba', true);

  const root = new TransformNode(options.name ?? 'beast', scene);
  const body = new TransformNode('beast-body', scene);
  body.parent = root;

  // Barrel chest, tapering to the haunches.
  box(scene, 'chest', { width: 0.52, height: 0.46, depth: 0.62 },
    new Vector3(0, 0.78, 0.16), hide, body);
  box(scene, 'hips', { width: 0.46, height: 0.42, depth: 0.44 },
    new Vector3(0, 0.74, -0.32), hide, body);
  box(scene, 'belly', { width: 0.44, height: 0.16, depth: 0.9 },
    new Vector3(0, 0.6, -0.06), dark, body);

  /* Legs. Front and back are separate pivots so they can swing in an actual
     gait: fronts and backs oppose each other, diagonals move together. */
  const legs: TransformNode[] = [];
  for (const [name, x, z] of [
    ['fl', -0.2, 0.38], ['fr', 0.2, 0.38], ['bl', -0.18, -0.4], ['br', 0.18, -0.4]
  ] as const) {
    const hip = new TransformNode(`beast-leg-${name}`, scene);
    hip.position.set(x, 0.62, z);
    hip.parent = body;
    box(scene, 'upper', { width: 0.15, height: 0.34, depth: 0.17 },
      new Vector3(0, -0.17, 0), hide, hip);
    box(scene, 'lower', { width: 0.12, height: 0.3, depth: 0.13 },
      new Vector3(0, -0.46, 0), dark, hip);
    box(scene, 'paw', { width: 0.16, height: 0.1, depth: 0.22 },
      new Vector3(0, -0.61, 0.03), dark, hip);
    legs.push(hip);
  }

  // --- head, carried forward and low, the way a hunting animal carries it ---
  const head = new TransformNode('beast-head', scene);
  head.position.set(0, 0.98, 0.5);
  head.parent = body;

  box(scene, 'skull', { width: 0.32, height: 0.3, depth: 0.34 },
    new Vector3(0, 0, 0), hide, head);
  box(scene, 'muzzle', { width: 0.18, height: 0.16, depth: 0.26 },
    new Vector3(0, -0.06, 0.26), dark, head);
  // Teeth: a thin pale strip under the muzzle. Tiny, and it is the difference
  // between a snout and a mouth.
  box(scene, 'teeth', { width: 0.14, height: 0.035, depth: 0.2 },
    new Vector3(0, -0.14, 0.27), claw, head);

  for (const side of [-1, 1]) {
    box(scene, 'ear', { width: 0.09, height: 0.16, depth: 0.05 },
      new Vector3(side * 0.11, 0.2, -0.02), dark, head);
    // Eyes glow faintly, so a beast in a dim forest is something you notice
    // before it reaches you.
    const eye = MeshBuilder.CreateBox('eye', { width: 0.05, height: 0.035, depth: 0.02 }, scene);
    eye.position.set(side * 0.085, 0.03, 0.172);
    const glow = material(scene, '#ffb347');
    glow.emissiveColor = Color3.FromHexString('#ff9a2e').scale(0.75);
    eye.material = glow;
    eye.isPickable = false;
    eye.parent = head;
  }

  // Tail. Parented to the body so it swings with the gait bob.
  const tail = new TransformNode('beast-tail', scene);
  tail.position.set(0, 0.86, -0.52);
  tail.parent = body;
  box(scene, 'tail', { width: 0.11, height: 0.11, depth: 0.4 },
    new Vector3(0, 0, -0.2), hide, tail);

  if (options.shadows != null) {
    for (const mesh of root.getChildMeshes()) {
      if (/chest|hips|skull|upper/.test(mesh.name)) options.shadows.addShadowCaster(mesh);
    }
  }

  /* Reuses FigureRig: the four legs map onto the two arm and two leg slots as
     diagonal pairs (front-left with back-right), which is exactly the pairing a
     four-legged gait wants — so poseWalk drives a beast correctly with no
     second animation function. */
  return {
    root, body, head,
    leftArm: legs[0]!, rightArm: legs[1]!,
    leftLeg: legs[3]!, rightLeg: legs[2]!,
    phase: 0
  };
}

/**
 * Moves the limbs.
 *
 * The phase advances with **distance travelled**, not with elapsed time, so
 * the feet keep pace with the ground instead of skating — which is the thing
 * that makes a walk cycle look wrong even when everything else is right.
 */
export function poseWalk(rig: FigureRig, speed: number, dt: number): void {
  const moving = speed > 0.15;

  if (moving) {
    // Roughly one full stride per 1.7 m. Tuned by eye against the walk speed.
    rig.phase += (speed * dt) * (Math.PI * 2 / 1.7);
  } else {
    // Settle back to standing rather than freezing mid-stride.
    rig.phase += dt * 6;
  }

  const swing = moving ? Math.min(0.72, 0.28 + speed * 0.1) : 0;
  const s = Math.sin(rig.phase);

  rig.leftLeg.rotation.x = s * swing;
  rig.rightLeg.rotation.x = -s * swing;
  // Arms counter-swing against the legs, which is what people actually do.
  rig.leftArm.rotation.x = -s * swing * 0.8;
  rig.rightArm.rotation.x = s * swing * 0.8;

  /* A small rise and fall at twice stride frequency. Barely visible on its own
     and immediately obvious by its absence. */
  rig.body.position.y = moving ? Math.abs(Math.cos(rig.phase)) * 0.035 : 0;

  if (!moving) {
    // Standing: a slow breath, so nobody looks switched off.
    const breathe = Math.sin(rig.phase * 0.22) * 0.012;
    rig.body.position.y = breathe;
    rig.leftArm.rotation.x = breathe * 0.6;
    rig.rightArm.rotation.x = breathe * 0.6;
  }
}
